import { faArrowAltCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowAltCircleLeft'
import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload'
import { faFileCsv } from '@fortawesome/pro-regular-svg-icons/faFileCsv'
import { faUpload } from '@fortawesome/pro-regular-svg-icons/faUpload'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import axios from 'axios'
import classNames from 'classnames'
import startCase from 'lodash/startCase'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'

import { GET_LIST, GET_ONE, useClient } from '@peracto/client'
import { useConfig } from '@peracto/peracto-config'
import { Group, Heading, HydraTable, Modal, TimeAgo } from '@peracto/peracto-ui'

import {
    DEFAULT_ERROR_MESSAGE,
    POLL_INTERVAL_MS,
    PROMOTIONS_API_IDENTIFIER,
    PROMOTIONS_IMPORT_ACTION_GUIDE,
    PROMOTIONS_IMPORT_COLUMN_GUIDE,
    axiosHydraMessage,
    exampleCsvDataHref,
    formatPromotionsDataTypeLabel,
    getPromotionsImportManagementOption,
    importLogId,
    isCreatedWithinLast24Hours,
} from '../shared'

const promotionsImportOption = getPromotionsImportManagementOption(PROMOTIONS_API_IDENTIFIER)

const PromotionsManageDataList = () => {
    const { client } = useClient()
    const config = useConfig()
    const apiUrl = config.get('api')
    const formatDate = config.get('user_format_date', config.get('format_date'))
    const fileInputRef = useRef(null)
    const pendingUploadFile = useRef(null)
    const [chosenFileLabel, setChosenFileLabel] = useState('')
    const [hasPendingFile, setHasPendingFile] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [tableKey, setTableKey] = useState(0)
    const [listRows, setListRows] = useState([])

    const clearPendingFile = useCallback(() => {
        pendingUploadFile.current = null
        setChosenFileLabel('')
        setHasPendingFile(false)
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }, [])

    const [selectedColumns, onSelectColumn] = useState({})
    const [guideModalOpen, setGuideModalOpen] = useState(false)

    const shouldPoll = useMemo(
        () =>
            listRows.some(
                (row) => row?.status === 'processing' && isCreatedWithinLast24Hours(row?.createdAt)
            ),
        [listRows]
    )

    const downloadUploadedFile = useCallback(
        async (id) => {
            try {
                const { data: downloadData } = await client(GET_ONE, 'import-logs', {
                    id: `/import-logs/${id}/pre-signed-url`,
                })

                if (downloadData?.downloadUrl) {
                    window.location.href = downloadData.downloadUrl
                } else {
                    toast.error(DEFAULT_ERROR_MESSAGE)
                }
            } catch (e) {
                console.error(e)
                const status = e?.status ?? e?.error?.status ?? e?.response?.status
                const hydraDescription = e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : null

                if (status === 400) {
                    toast.error(
                        hydraDescription ?? 'File is not ready to download yet. Please try again.'
                    )
                } else {
                    toast.error(hydraDescription ?? DEFAULT_ERROR_MESSAGE)
                }
            }
        },
        [client]
    )

    const columns = useMemo(
        () => [
            {
                Header: 'ID',
                id: 'importId',
                accessor: (row) => importLogId(row),
                disableSorting: true,
                Cell: ({ cell: { value }, row }) => (
                    <span data-testid={`row-${row.index}-id`}>{value}</span>
                ),
                selectable: false,
            },
            {
                Header: 'Original file',
                accessor: 'originalFilename',
                disableSorting: true,
                Cell: ({ cell: { value } }) => (value ? value : '-'),
                selectable: false,
            },
            {
                Header: 'Type',
                accessor: 'type',
                sortType: 'basic',
                Cell: ({ cell: { value } }) => formatPromotionsDataTypeLabel(value),
                selectable: false,
                disableSorting: true,
            },
            {
                Header: 'Status',
                accessor: 'status',
                disableSorting: true,
                Cell: ({ cell: { value } }) => (
                    <span
                        className={classNames('badge', 'badge-pill', {
                            'badge-info': value === 'processing',
                            'badge-success': value === 'complete',
                            'badge-danger': value === 'error',
                        })}
                    >
                        {startCase(value)}
                    </span>
                ),
                selectable: false,
            },
            {
                Header: 'Row Count',
                id: 'rowCount',
                accessor: 'rowCount',
                disableSorting: true,
                Cell: ({ cell: { value } }) => (value != null ? value : '-'),
                selectable: false,
            },
            {
                Header: 'Errors',
                id: 'errorRowCount',
                accessor: 'errorRowCount',
                disableSorting: true,
                Cell: ({ cell: { value } }) => (value != null ? value : '-'),
                selectable: false,
            },
            {
                Header: 'Created',
                accessor: 'createdAt',
                disableSorting: true,
                Cell: ({ cell: { value } }) => {
                    if (!value) return '-'
                    const date = new Date(value)
                    if (!date.getDate()) return '-'
                    return <TimeAgo title={formatDate(date)} date={value} />
                },
                selectable: false,
            },
            {
                id: 'viewImport',
                Header: '',
                accessor: () => null,
                disableSorting: true,
                Cell: ({ row }) => {
                    const id = importLogId(row.original)
                    if (!id) return null
                    return (
                        <Link
                            to={`/manage-promotions-data/${id}`}
                            className="p-0 btn btn-sm btn-link text-primary"
                            data-testid={`row-${row.index}-view-import`}
                        >
                            View import
                        </Link>
                    )
                },
                selectable: false,
            },
            {
                Header: 'Actions',
                id: 'actions',
                accessor: () => null,
                disableSorting: true,
                Cell: ({ row }) => {
                    const id = importLogId(row.original)
                    if (!id) return '-'
                    return (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => downloadUploadedFile(id)}
                            data-testid={`row-${row.index}-download`}
                        >
                            <FontAwesomeIcon icon={faDownload} className="mr-2" />
                            Download CSV
                        </button>
                    )
                },
                selectable: false,
            },
        ],
        [formatDate, downloadUploadedFile]
    )

    const onFetchData = useCallback(
        async (options) => {
            const result = await client(GET_LIST, 'import-logs', options)
            setListRows(Array.isArray(result?.data) ? result.data : [])
            return result
        },
        [client]
    )

    useEffect(() => {
        if (!shouldPoll) return undefined

        const id = setInterval(() => {
            setTableKey((k) => k + 1)
        }, POLL_INTERVAL_MS)

        return () => clearInterval(id)
    }, [shouldPoll])

    const handleFileChosen = (e) => {
        const file = e.currentTarget.files?.[0]
        e.target.value = ''
        if (!file) return
        pendingUploadFile.current = file
        setChosenFileLabel(file.name)
        setHasPendingFile(true)
    }

    const handleUploadFile = async () => {
        const file = pendingUploadFile.current
        if (!file) {
            toast.info('Choose a CSV file first.')
            return
        }

        const formData = new FormData()
        formData.append('type', PROMOTIONS_API_IDENTIFIER)
        formData.append('file', file)

        setUploading(true)
        try {
            await axios.post(`${apiUrl}/import-logs`, formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            })
            toast.success(promotionsImportOption.uploadSuccessMessage)
            clearPendingFile()
            setTableKey((k) => k + 1)
        } catch (err) {
            console.error(err)
            toast.error(axiosHydraMessage(err) ?? DEFAULT_ERROR_MESSAGE)
        } finally {
            setUploading(false)
        }
    }

    return (
        <>
            <Helmet>
                <title>{`Bulk manage promotions data - ${promotionsImportOption.listTitle} | Peracto`}</title>
            </Helmet>
            <Heading name="Bulk Manage Promotions Data">
                <div className="flex-wrap ml-2 d-flex align-items-center">
                    <Link className="mb-2 mr-2 btn btn-outline-primary mb-sm-0" to="/promotions">
                        <FontAwesomeIcon icon={faArrowAltCircleLeft} className="mr-2" />
                        Back to promotions
                    </Link>
                </div>
            </Heading>

            <Modal
                isVisible={guideModalOpen}
                title="Promotions Import Guide"
                className="promotions-import-guide-modal"
                close={() => setGuideModalOpen(false)}
                buttons={[
                    {
                        type: 'btn-outline-primary',
                        text: 'Close',
                        action: () => setGuideModalOpen(false),
                    },
                ]}
            >
                <div
                    className="promotions-import-guide-modal-body"
                    style={{ maxHeight: '70vh', overflowY: 'auto' }}
                >
                    <p className="text-muted mb-3">
                        Bulk import supports <strong>create</strong>, <strong>amend</strong>, and{' '}
                        <strong>delete</strong> via CSV. Use <strong>Promotions Export</strong> to
                        download the current dataset, edit it, and upload it here again. Your file
                        must be comma-separated CSV; from Numbers or Excel use{' '}
                        <strong>CSV (UTF-8)</strong>. The header row must match the documented columns
                        (see below). One promotion per row. A trailing comma after{' '}
                        <code>promotionCodes</code> is fine if your export includes it.
                    </p>
                    <h3 className="mb-3 h6 font-weight-bold">Row actions</h3>
                    <div className="table-responsive mb-4">
                        <table className="table table-sm table-bordered bg-white">
                            <thead>
                                <tr>
                                    <th scope="col" style={{ width: '7rem' }}>
                                        action
                                    </th>
                                    <th scope="col">Meaning</th>
                                </tr>
                            </thead>
                            <tbody>
                                {PROMOTIONS_IMPORT_ACTION_GUIDE.map(({ action, description }) => (
                                    <tr key={action}>
                                        <td>
                                            <code>{action}</code>
                                        </td>
                                        <td className="text-muted small">{description}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <h3 className="mb-3 h6 font-weight-bold">Columns (in order)</h3>
                    <div className="table-responsive">
                        <table className="table table-sm table-bordered bg-white">
                            <thead>
                                <tr>
                                    <th scope="col" style={{ width: '13rem' }}>
                                        Column
                                    </th>
                                    <th scope="col" style={{ width: '5rem' }}>
                                        Required
                                    </th>
                                    <th scope="col" style={{ width: '11rem' }}>
                                        Format
                                    </th>
                                    <th scope="col" style={{ minWidth: '14rem' }}>
                                        Example
                                    </th>
                                    <th scope="col">Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {PROMOTIONS_IMPORT_COLUMN_GUIDE.map(
                                    ({ column, required, format, example, description }) => (
                                        <tr key={column}>
                                            <td>
                                                <code>{column}</code>
                                            </td>
                                            <td className="text-muted small">
                                                {required ? 'Yes' : 'No'}
                                            </td>
                                            <td className="text-muted small">
                                                <code>{format}</code>
                                            </td>
                                            <td className="text-muted small">
                                                <code
                                                    style={{
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {example}
                                                </code>
                                            </td>
                                            <td className="text-muted small">{description}</td>
                                        </tr>
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal>

            <div className="row">
                <div className="col">
                    <Group id="promotions-manage-data-upload" name="Manage Data">
                        <div className="mb-4">
                            <p className="mb-0 text-muted">
                                {promotionsImportOption.instructionsBeforeGuide}
                                <button
                                    type="button"
                                    className="btn btn-link text-primary p-0 align-baseline"
                                    onClick={() => setGuideModalOpen(true)}
                                    data-testid="view-promotions-import-guide"
                                >
                                    {promotionsImportOption.guideLinkLabel}
                                </button>
                                {promotionsImportOption.instructionsAfterGuide}
                                {promotionsImportOption.exampleCsv ? (
                                    <>
                                        {' '}
                                        Example file available{' '}
                                        <a
                                            href={
                                                promotionsImportOption.exampleCsv.href ??
                                                exampleCsvDataHref(
                                                    promotionsImportOption.exampleCsv.content
                                                )
                                            }
                                            download={promotionsImportOption.exampleCsv.download}
                                            className="text-primary"
                                        >
                                            here
                                        </a>
                                        .
                                    </>
                                ) : null}
                            </p>
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            className="d-none"
                            onChange={handleFileChosen}
                            data-testid="bulk-promotions-csv-input"
                        />

                        <div className="flex-wrap mb-3 d-flex align-items-center">
                            <button
                                type="button"
                                className="mb-2 mr-3 btn btn-outline-primary mb-sm-0"
                                disabled={uploading}
                                onClick={() => fileInputRef.current?.click()}
                                data-testid="choose-bulk-promotions-csv"
                            >
                                <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
                                Choose File
                            </button>
                            <span className="mb-2 text-muted mb-sm-0">
                                {chosenFileLabel || 'No File Selected'}
                            </span>
                        </div>

                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={uploading || !hasPendingFile}
                            onClick={handleUploadFile}
                            data-testid="upload-bulk-promotions-csv"
                        >
                            <FontAwesomeIcon icon={faUpload} className="mr-2" />
                            {uploading ? 'Uploading...' : 'Upload File'}
                        </button>
                    </Group>

                    <div className="card card--transparent">
                        <div className="card-body">
                            <h2 className="mb-4 h5">
                                {promotionsImportOption.historyTitle ??
                                    promotionsImportOption.listTitle}
                            </h2>
                            <HydraTable
                                key={tableKey}
                                columns={columns}
                                selectedColumns={selectedColumns}
                                onFetchData={onFetchData}
                                onSelectColumn={onSelectColumn}
                                alwaysFilter={{ type: PROMOTIONS_API_IDENTIFIER }}
                                noResultsMessage={
                                    <p className="mb-0 text-muted">
                                        {promotionsImportOption.noResultsMessage}
                                    </p>
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default PromotionsManageDataList
